import Header from "../components/shared/Header";
import Footer from "../components/shared/Footer";
import SEOHead from "../components/shared/SEOHead";
import Breadcrumbs, { generateBreadcrumbSchema } from "../components/shared/Breadcrumbs";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Truck, Clock, Shield, Package, Phone } from "lucide-react";
import { createServiceSchema, createFAQSchema } from "../seo/schemas";

const ManAndVanPage = () => {
  const features = [
    { icon: Clock, title: "Same Day Available", desc: "Need a van today? Call us — we often have same-day availability" },
    { icon: Truck, title: "Various Van Sizes", desc: "From small Luton vans to large removal trucks" },
    { icon: Shield, title: "Fully Insured", desc: "All moves insured up to £50,000 as standard" },
    { icon: Package, title: "Load & Unload", desc: "Our team does the heavy lifting — you just tell us where" },
  ];

  const useCases = [
    "Moving a few items to a new flat",
    "Student moves to and from university",
    "eBay, Gumtree, or Facebook Marketplace deliveries",
    "Single item collection and delivery",
    "Small office or studio moves",
    "Furniture deliveries from stores",
    "Clearance and disposal runs",
    "Emergency or same-day moves",
  ];

  const breadcrumbs = [
    { label: "Services", href: "/services" },
    { label: "Man and Van", href: "/services/man-and-van" },
  ];

  const pageFaqs = [
    { q: "How much does a man and van cost in Cardiff?", a: "Our man and van service in Cardiff starts from £50 per hour. Most jobs cost between £80 and £250 depending on distance, number of items, and any access challenges. Get an instant estimate online." },
    { q: "Can I book a man and van for today?", a: "In many cases, yes. We keep flexible availability for same-day and next-day man and van bookings. Call 07346 209593 to check. A small surcharge may apply for same-day requests." },
    { q: "What's the difference between man and van and a full removal?", a: "Man and van is ideal for smaller moves — a few items, single rooms, or student moves. A full removal service includes a larger team, packing materials, furniture protection, and is designed for whole-property moves." },
    { q: "Is the man and van service insured?", a: "Yes, all our services including man and van are fully insured up to £50,000. Your belongings are covered during loading, transit, and unloading." },
    { q: "Can your man and van team help carry heavy items?", a: "Absolutely. Our team is trained to handle heavy and awkward items including sofas, washing machines, wardrobes, and more. Just let us know what you need moved when booking." },
  ];

  const jsonLd = [
    createServiceSchema(
      "Man and Van Cardiff",
      "Affordable man and van hire in Cardiff and South Wales. Same-day availability, fully insured, no hidden fees. Single items to small moves.",
      "/services/man-and-van"
    ),
    createFAQSchema(pageFaqs),
    generateBreadcrumbSchema(breadcrumbs),
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Man and Van Cardiff | Same Day Available | Hitech Transit"
        description="Affordable man and van hire in Cardiff & South Wales. Same-day availability, fully insured, no hidden fees. From single items to small moves. Call 07346 209593."
        canonical="/services/man-and-van"
        jsonLd={jsonLd}
      />
      <Header />

      <main className="pt-20">
        <Breadcrumbs items={breadcrumbs} />

        {/* Hero */}
        <section className="bg-[#0F172A] py-20">
          <div className="container-max px-4 sm:px-6 lg:px-8">
            <div className="max-w-3xl">
              <span className="text-xs tracking-[0.2em] uppercase font-bold text-[#FF5A1F] mb-4 block">
                Man and Van
              </span>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-['Outfit'] font-bold text-white tracking-tight mb-6">
                Man and Van Service in Cardiff
              </h1>
              <p className="text-slate-300 text-lg mb-8">
                Need to move a few items without the cost of a full removal? Our man and van service is perfect for smaller jobs — fast, affordable, and fully insured.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link to="/instant-quote" className="btn-primary inline-flex items-center justify-center gap-2" data-testid="man-van-quote-btn">
                  Get a Quote <ArrowRight className="w-5 h-5" />
                </Link>
                <a href="tel:07346209593" className="bg-white/10 backdrop-blur-sm text-white font-semibold px-6 py-3 inline-flex items-center justify-center gap-2 hover:bg-white/20 transition-colors border border-white/20">
                  <Phone className="w-5 h-5" /> 07346 209593
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
              {features.map((f) => (
                <div key={f.title} className="p-6 border border-slate-200 hover:border-[#FF5A1F] transition-colors">
                  <f.icon className="w-8 h-8 text-[#FF5A1F] mb-4" />
                  <h3 className="text-lg font-['Outfit'] font-bold text-[#0F172A] mb-2">{f.title}</h3>
                  <p className="text-slate-600 text-sm">{f.desc}</p>
                </div>
              ))}
            </div>

            <div className="max-w-3xl">
              {/* Answer-first for GEO */}
              <div className="bg-slate-50 border-l-4 border-[#FF5A1F] p-6 mb-10">
                <p className="text-slate-700 leading-relaxed">
                  <strong>Hitech Transit's man and van service</strong> is an affordable way to move smaller loads in Cardiff and South Wales. Starting from £50/hour, our uniformed driver helps load, transport, and unload your items. Fully insured, no hidden fees.
                </p>
              </div>

              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                What Our Man and Van Service Is For
              </h2>
              <div className="grid sm:grid-cols-2 gap-3 mb-10">
                {useCases.map((item) => (
                  <div key={item} className="flex items-start gap-3">
                    <Check className="w-5 h-5 text-[#FF5A1F] mt-0.5 flex-shrink-0" />
                    <span className="text-slate-700 text-sm">{item}</span>
                  </div>
                ))}
              </div>

              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                How It Works
              </h2>
              <div className="space-y-4 mb-10">
                {[
                  { step: "1", title: "Tell Us What You Need", desc: "Call us or use the online form. Let us know what you're moving, the pickup and delivery locations." },
                  { step: "2", title: "Get Your Price", desc: "We'll give you a clear, upfront price. No hidden fees, no surprises." },
                  { step: "3", title: "We Collect and Deliver", desc: "Our driver arrives on time, helps load everything, and delivers safely to your destination." },
                ].map((s) => (
                  <div key={s.step} className="flex gap-4">
                    <div className="w-10 h-10 bg-[#FF5A1F] text-white font-bold flex items-center justify-center flex-shrink-0">
                      {s.step}
                    </div>
                    <div>
                      <h3 className="font-semibold text-[#0F172A]">{s.title}</h3>
                      <p className="text-slate-600 text-sm">{s.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Pricing Guide */}
              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-4">
                Man and Van Pricing
              </h2>
              <div className="grid sm:grid-cols-3 gap-4 mb-10">
                {[
                  { label: "Single Item", price: "from £50", desc: "One large item, local delivery" },
                  { label: "Half Load", price: "from £100", desc: "Several items, up to half a van" },
                  { label: "Full Van", price: "from £180", desc: "Full van load, ideal for studio/1-bed" },
                ].map((p) => (
                  <div key={p.label} className="border border-slate-200 p-6 text-center">
                    <div className="text-sm font-semibold text-slate-500 mb-2">{p.label}</div>
                    <div className="text-3xl font-['Outfit'] font-bold text-[#FF5A1F] mb-2">{p.price}</div>
                    <div className="text-xs text-slate-500">{p.desc}</div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-slate-500 mb-10">
                Prices are estimates for local Cardiff moves. Final price depends on distance, access, and items.
                Use our <Link to="/instant-quote" className="text-[#FF5A1F] font-medium hover:underline">online calculator</Link> for a personalised quote.
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="section-padding bg-slate-50">
          <div className="container-max">
            <div className="max-w-3xl">
              <h2 className="text-2xl font-['Outfit'] font-bold text-[#0F172A] mb-8">
                Frequently Asked Questions About Man and Van
              </h2>
              <div className="space-y-6">
                {pageFaqs.map((faq, i) => (
                  <div key={`faq-${i}`} className="bg-white border border-slate-200 p-6">
                    <h3 className="text-lg font-semibold text-[#0F172A] mb-2">{faq.q}</h3>
                    <p className="text-slate-600 leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-[#FF5A1F]">
          <div className="container-max text-center">
            <h2 className="text-3xl md:text-4xl font-['Outfit'] font-bold text-white mb-4">
              Need a Man and Van Today?
            </h2>
            <p className="text-white/90 text-lg max-w-xl mx-auto mb-8">
              Call now for same-day availability or get a free instant quote online.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/instant-quote" className="bg-white text-[#FF5A1F] font-semibold px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-slate-50 transition-colors">
                Get My Free Quote <ArrowRight className="w-5 h-5" />
              </Link>
              <a href="tel:07346209593" className="border-2 border-white text-white font-semibold px-8 py-4 inline-flex items-center justify-center gap-2 hover:bg-white/10 transition-colors">
                <Phone className="w-5 h-5" /> 07346 209593
              </a>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ManAndVanPage;
